import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { ApiError } from "@njbsoft/api-client";
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  DataTable,
  EmptyValue,
  PageHeader,
  PageSkeleton,
  Tabs,
  type TabDef,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  useFormTemplate,
  useTemplateVersions,
  usePublishDraft,
  useRetireVersion,
  type FormTemplateVersion,
} from "@/hooks/use-company-form-template";
import { formatDateTime } from "@/lib/dates";

type DetailTab = "settings" | "versions";

const ALLOWED_TABS: DetailTab[] = ["settings", "versions"];

interface DetailSearch {
  tab?: DetailTab;
}

export const Route = createFileRoute("/_company/company/forms/$templateId/")({
  component: FormTemplateDetailPage,
  validateSearch: (search: Record<string, unknown>): DetailSearch => ({
    tab: typeof search.tab === "string" ? ALLOWED_TABS.find((x) => x === search.tab) : undefined,
  }),
});

const versionColumnHelper = createColumnHelper<FormTemplateVersion>();

function FormTemplateDetailPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { templateId } = Route.useParams();
  const { tab } = Route.useSearch();
  const toasts = useToast();
  const isAdmin = useIsCompanyAdmin();

  const templateQuery = useFormTemplate(templateId);
  const versionsQuery = useTemplateVersions(templateId);

  const [publishConfirm, setPublishConfirm] = useState(false);
  const [retireTarget, setRetireTarget] = useState<FormTemplateVersion | null>(null);

  const publishMutation = usePublishDraft(templateId);
  const retireMutation = useRetireVersion(templateId, retireTarget?.id ?? "");

  const activeTab: DetailTab = tab ?? "settings";
  const setActiveTab = (next: DetailTab) => {
    void navigate({
      to: "/company/forms/$templateId",
      params: { templateId },
      search: next === "settings" ? {} : { tab: next },
      replace: true,
    });
  };

  const versions = useMemo(() => versionsQuery.data?.items ?? [], [versionsQuery.data?.items]);
  const publishedVersion = useMemo(
    () =>
      versions.filter((v) => v.status === "published").sort((a, b) => b.version - a.version)[0] ??
      null,
    [versions],
  );

  const versionColumns = useMemo(
    () => [
      versionColumnHelper.accessor("version", {
        id: "version",
        header: t("forms.versionLabel"),
        enableSorting: true,
        cell: (ctx) => `v${ctx.getValue()}`,
      }),
      versionColumnHelper.accessor("status", {
        id: "status",
        header: t("forms.columns.status"),
        enableSorting: true,
        cell: (ctx) => {
          const status = ctx.getValue();
          const variant =
            status === "published" ? "success" : status === "draft" ? "warning" : "default";
          return <Badge variant={variant}>{t(`forms.status.${status}`)}</Badge>;
        },
      }),
      versionColumnHelper.accessor((row) => row.published_at ?? null, {
        id: "published_at",
        header: t("forms.submissionColumns.receivedAt"),
        enableSorting: true,
        cell: (ctx) => (ctx.getValue() ? formatDateTime(ctx.getValue()) : <EmptyValue />),
      }),
    ],
    [t],
  );

  if (templateQuery.isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  const template = templateQuery.data;
  if (templateQuery.isError || !template) {
    return (
      <div className="p-8">
        <Breadcrumbs items={[{ label: t("forms.title"), to: "/company/forms" }]} />
        <PageHeader title={t("forms.loadDetailFailed")} />
      </div>
    );
  }

  const handlePublish = () => {
    setPublishConfirm(false);
    publishMutation.mutate(undefined, {
      onSuccess: () => {
        toasts.success("company:forms.publishSuccess");
        setActiveTab("versions");
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : undefined;
        if (code === "company_supplement_conflict") {
          toasts.error("company:forms.categoryConflict");
          return;
        }
        // A 422 publish-gate failure must be resolved in the builder problems
        // dock — route the author there.
        if (err instanceof ApiError && err.status === 422) {
          void navigate({ to: "/company/forms/$templateId/builder", params: { templateId } });
          return;
        }
        toasts.apiError(code);
      },
    });
  };

  const handleRetire = () => {
    if (!retireTarget) return;
    retireMutation.mutate(undefined, {
      onSuccess: () => {
        toasts.success("company:forms.retireSuccess");
        setRetireTarget(null);
      },
      onError: (err) => {
        toasts.apiError(err instanceof ApiError ? err.code : undefined);
        setRetireTarget(null);
      },
    });
  };

  const tabs: TabDef<DetailTab>[] = [
    { value: "settings", label: t("forms.settingsTab") },
    { value: "versions", label: t("forms.versions"), count: versions.length },
  ];

  return (
    <div className="space-y-6 p-8">
      <Breadcrumbs
        items={[{ label: t("forms.title"), to: "/company/forms" }, { label: template.title }]}
      />
      <PageHeader
        title={template.title}
        actions={
          <div className="flex items-center gap-2">
            {/* Every WRITE on a supplement — authoring the draft in the
                builder, publishing it, retiring a published version — is
                admin-only, mirroring the ft_/ftv_company_insert and _update
                RLS arms. A tester reads the family and its versions. */}
            {isAdmin && (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate({
                    to: "/company/forms/$templateId/builder",
                    params: { templateId },
                  })
                }
              >
                {t("forms.openBuilder")}
              </Button>
            )}
            {isAdmin && template.has_draft && (
              <Button onClick={() => setPublishConfirm(true)} disabled={publishMutation.isPending}>
                {t("forms.publish")}
              </Button>
            )}
            {isAdmin && publishedVersion && (
              <Button
                variant="danger"
                onClick={() => setRetireTarget(publishedVersion)}
                disabled={retireMutation.isPending}
              >
                {t("forms.retire")}
              </Button>
            )}
          </div>
        }
      />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        ariaLabel={template.title}
      />

      {activeTab === "settings" && (
        <Card title={t("forms.createDialogTitle")}>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-muted">{t("forms.slugLabel")}</dt>
              <dd className="font-mono text-on-surface">{template.slug}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("forms.targetKindLabel")}</dt>
              <dd className="text-on-surface">{t(`forms.targetKind.${template.target_kind}`)}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("forms.deviceCategoryLabel")}</dt>
              <dd className="text-on-surface">
                {template.device_category
                  ? t(`device_category.${template.device_category}`, { ns: "vocabulary" })
                  : t("forms.deviceCategory.all")}
              </dd>
              <p className="mt-1 text-xs text-muted">{t("forms.deviceCategoryHint")}</p>
            </div>
          </dl>
        </Card>
      )}

      {activeTab === "versions" && (
        <Card>
          <DataTable columns={versionColumns} data={versions} manualSorting={false} />
        </Card>
      )}

      <ConfirmDialog
        open={publishConfirm}
        title={t("forms.publish")}
        message={t("forms.publishConfirmBody")}
        confirmLabel={t("forms.publish")}
        cancelLabel={t("common:cancel")}
        variant="primary"
        confirmDisabled={publishMutation.isPending}
        onConfirm={handlePublish}
        onCancel={() => setPublishConfirm(false)}
      />

      <ConfirmDialog
        open={retireTarget !== null}
        title={t("forms.retire")}
        message={t("forms.retireConfirmBody")}
        confirmLabel={t("forms.retire")}
        cancelLabel={t("common:cancel")}
        variant="danger"
        confirmDisabled={retireMutation.isPending}
        onConfirm={handleRetire}
        onCancel={() => setRetireTarget(null)}
      />
    </div>
  );
}
